import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import axiosOriginal from 'axios';
import { useDropzone } from 'react-dropzone';
import {
  CircularProgress,
  Fade, IconButton, Link, List, ListItem, ListItemText, Modal, Paper, Tooltip,
} from '@material-ui/core';
// icons
import DeleteIcon from '@material-ui/icons/Delete';

import Button from '../Button';
// La firma la da la API y necesita el token, así que va por la instancia
// configurada; el PUT a S3 sigue por axios crudo, sin headers de la app.
import axios from '../../utils/axios';
import bytesToSize from '../../utils/bytesToSize';

import useStyles from './styles';

function ExampleImageQuestion({
  open,
  setOpen,
  file,
  setFile,
  handleOnChange,
  setImage,
  image,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  // En creación todavía no hay task, así que la API sube la imagen a `drafts`
  // y la reubica bajo la encuesta cuando se guarda.
  const { taskId } = useParams();

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  // El basurero no borra al toque: deja la baja pendiente de confirmar con el
  // mismo botón que se usa para subir, y hasta entonces no se toca la pregunta.
  const [removePending, setRemovePending] = useState(false);

  const handleRemoveMedia = () => {
    setFile({});
    setError(null);
    if (image?.url) {
      setRemovePending(true);
    }
  };

  const handleClose = () => {
    // Cerrar sin confirmar cancela la baja
    setRemovePending(false);
    setOpen(false);
  };

  // La imagen que ya tenía la pregunta se muestra como seleccionada hasta que se
  // elija un archivo nuevo o se marque para sacarla.
  const savedImage = !file.name && !removePending && image?.url ? image : null;
  const savedImageName = savedImage ? String(savedImage.name || '').split('/').pop() : '';

  const resizeImage = (imageFile) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const maxSize = 720;
        // Limita el lado más largo a maxSize, conserva la proporción y no agranda imágenes chicas
        const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
        const width = Math.round(img.width * scale);
        const height = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          const resizedFile = new File([blob], imageFile.name, { type: imageFile.type });
          resolve(resizedFile);
        }, imageFile.type);
      };
      img.onerror = () => reject(new Error(t('fileDropzone.errorUpload')));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error(t('fileDropzone.errorUpload')));
    reader.readAsDataURL(imageFile);
  });

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/*': [],
    },
    onDrop: async (acceptedFiles) => {
      setError(null);
      setRemovePending(false);
      try {
        const resizedFile = await resizeImage(acceptedFiles[0]);
        setFile(resizedFile);
      } catch (err) {
        setError(err.message);
      }
    },
  });

  const handleUpload = async () => {
    setLoading(true);
    try {
      const uploadData = {
        surveyId: taskId || null,
        fileName: file.name,
        fileType: file.type,
      };
      const response = await axios.post('/api/tasks/signS3', uploadData);
      const options = {
        headers: {
          'Content-Type': file.type,
        },
      };

      const { returnData } = response.data.data;
      const { signedRequest, imageKey, imageUrl } = returnData;
      const uploadResponse = await axiosOriginal.put(
        signedRequest,
        file,
        options,
      );
      if (uploadResponse.status === 200) {
        const event = {
          target: {
            id: 'imageKey',
            value: imageKey,
            imageUrl,
          },
        };
        await setImage({
          url: imageUrl,
          name: file.name,
          size: bytesToSize(file.size),
        });
        handleOnChange(event);
        setError('');
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      setError(t('fileDropzone.errorUpload'));
    }
    setLoading(false);
    setOpen(false);
  };

  /**
   * Confirma lo que quedó pendiente en el modal: subir el archivo recién elegido
   * o, si se apretó el basurero, sacarle la imagen a la pregunta. El archivo nuevo
   * tiene prioridad, igual que en lo que se muestra.
   */
  const handleConfirm = async () => {
    if (file.name) {
      await handleUpload();
      return;
    }

    setImage({});
    handleOnChange({
      target: {
        id: 'imageKey',
        value: null,
        imageUrl: null,
      },
    });
    setRemovePending(false);
    setOpen(false);
  };

  return (
    <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        className={classes.root}
    >
      <Fade in={open}>
          <Paper className={classes.paperContainer}>
              <Tooltip title={t('task.taskForm.deleteMedia')}>
                <IconButton
                  className={classes.deleteButton}
                  onClick={handleRemoveMedia}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
              <p>{t('task.taskForm.addMediaTitle')}</p>
              <div className={classes.dragAndDropContainer}>
                  <div {...getRootProps({ className: 'dropzone' })}>
                    <input {...getInputProps()} />
                    <div className={classes.dragAndDropZone}>
                      <p className={classes.dropText}>
                          {t('task.taskForm.addImageExampleParagraphOne')}
                          {' '}
                          <Link underline="always">{t('task.taskForm.search')}</Link>
                          {' '}
                          {t('task.taskForm.addImageExampleParagraphTwo')}
                      </p>
                    </div>
                  </div>
                  {file.name && (
                    <List className={classes.list}>
                      <ListItem>
                        <ListItemText
                          primary={file.name}
                          primaryTypographyProps={{ variant: 'h5' }}
                          secondary={bytesToSize(file.size)}
                        />
                      </ListItem>
                    </List>
                  )}
                  {savedImage && (
                    <List className={classes.list}>
                      <ListItem>
                        <img
                          className={classes.preview}
                          alt='reference-image'
                          src={savedImage.url}
                        />
                        <ListItemText
                          primary={savedImageName}
                          primaryTypographyProps={{ variant: 'h5' }}
                          secondary={savedImage.size || null}
                        />
                      </ListItem>
                    </List>
                  )}
                  {(file.name || removePending) && (
                    <Button
                      id="submit-ticket-btn"
                      onClick={handleConfirm}
                      text={loading ? <CircularProgress /> : t('fileDropzone.upload')}
                      height="40px"
                      width="130px"
                      backgroundColor='theme-secondary-blue'
                      color='theme-primary-white'
                      borderRadius='10px'
                    />
                  )}
                  {error ? (
                      <p className={classes.errorMessage}>{error}</p>
                  ) : null}
              </div>
          </Paper>
      </Fade>
    </Modal>
  );
}

ExampleImageQuestion.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  file: PropTypes.object.isRequired,
  setFile: PropTypes.func.isRequired,
  image: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
};

export default ExampleImageQuestion;
